import { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Divider } from 'semantic-ui-react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { TextField } from '@mui/material';
import { Checkbox } from 'semantic-ui-react'
import Box from '@mui/material/Box';
import Typography from "@mui/material/Typography";
import CloseIcon from '@mui/icons-material/Close';
import Modal from '@mui/material/Modal';
import Button from "@mui/material/Button";
import ButtonGroup from '@mui/material/ButtonGroup';
import UploadFile from '../UploadFile';
import SelectElement from '../SelectElement';
import MessageModal from 'app/components/MessageModal';
import classes from 'app/components/Modal/classes.module.scss';
import QRCodeGeneratorModal from './QRCodeGeneratorModal';
import QRCodeGeneratorProgressBar from './QRCodeGeneratorProgressBar';

import { CSV_DOWNLOAD_TYPE, DOWNLOAD_TYPES, STATUS } from 'apps/portal/utils/Constants';

import {
  getSimpleJobs
} from 'app/services/jobs';

import {
  uploadQRCodeTemplate,
  getQRCodeGenTemplates,
  getQRCodeGenJobs,
  generateQRCodeCards,
  removeQRCodeTemplates
} from 'app/actions/jobs'

import {
  downloadTypeOptions,
  formatOptions
} from 'app/utils/options';

import { getLocalizedDate } from 'shared/utils/Utils';

const getQRCodeRequestCreatedAt = request => {
  const time = new Date(request.created_at).getTime();
  return Number.isNaN(time) ? 0 : time;
}

const sortQRCodeRequestsByMostRecent = requests => {
  return [...(requests || [])].sort((a, b) => getQRCodeRequestCreatedAt(b) - getQRCodeRequestCreatedAt(a));
}

const ACTIVE_QR_CODE_REQUEST_STATUSES = [1, 2, 4];

const EmployeeQRGenerator = (props) => {
  const { isOpen, onClose, user } = props;
  const [loaded, setLoaded] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [showRemove, setShowRemove] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [selectedQRCodeRequest, setSelectedQRCodeRequest] = useState(null);

  const [jobOptions, setJobOptions] = useState([]);
  const [qrCodeGenJob, setQrCodeGenJob] = useState({ total: '', email: '', download_type: 0, template: '' });
  const [qrCodeGenJobs, setQrCodeGenJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(0);

  const [templateSelection, setTemplateSelection] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [templateDisplay, setTemplateDisplay] = useState([]);
  const [templateRemove, setTemplateRemove] = useState([]);

  const [templateFileSrc, setTemplateFileSrc] = useState(null);
  const [templateFile, setTemplateFile] = useState({});
  const [templateName, setTemplateName] = useState('');
  const [templatePsd, setTemplatePsd] = useState('');

  const [message, setMessage] = useState(null);
  const [removeMessage, setRemoveMessage] = useState(null);
  const [skipGeneratePDF, setSkipGeneratePDF] = useState(false);

  //const modalStyle = {};

  //modalStyle.justifyContent = 'end';
  //modalStyle.marginRight = '1.5rem';

  let boxClass = classes.paper,
    boxStyle = null,
    templateClass = classes.paper,
    headerVariant = 'h5';

  const textInput = {
    marginTop: "1rem",
    marginBottom: "1rem"
  }

  const hasActiveQRCodeRequest = qrCodeGenJobs.some(job => ACTIVE_QR_CODE_REQUEST_STATUSES.includes(job.status));
  const isCSVOnly = Number(qrCodeGenJob.download_type) === CSV_DOWNLOAD_TYPE;

  const fetchQRCodeGenJobs = useCallback(async () => {
    if (selectedJobId <= 0) {
      return;
    }

    const jobs = sortQRCodeRequestsByMostRecent(await getQRCodeGenJobs(selectedJobId));
    setQrCodeGenJobs(jobs);
    setSelectedQRCodeRequest(request => {
      if (!request) {
        return request;
      }

      return jobs.find(job => job.id === request.id) || request;
    });
  }, [selectedJobId])

  const fetchQRCodeTemplateSelection = useCallback(async () => {
    var selectionList = [];
    var dbSelection = await getQRCodeGenTemplates(selectedJobId);
    setTemplatePsd(dbSelection.link);
    if (dbSelection) {
      for (var selection of dbSelection.templates) {
        selectionList.push({
          label: selection.name,
          value: selection.id,
          template: selection
        })
      }

      setTemplateSelection(selectionList);
      setTemplateDisplay(selectionList);
      setTemplateRemove([]);
    }
  }, [selectedJobId])

  useEffect(() => {
    const init = async () => {
      if (!loaded) {
        var jobs = await getSimpleJobs();
        setJobOptions(formatOptions(jobs));

        setSelectedJobId(currentSelectedJobId => currentSelectedJobId > 0 ? currentSelectedJobId : jobs[0].id);
        setLoaded(true);
      }
    }

    init();
  }, [loaded])

  useEffect(() => {
    const init = async () => {
      if (selectedJobId > 0) {
        await Promise.all([
          fetchQRCodeGenJobs(),
          fetchQRCodeTemplateSelection()
        ]);
      }
    }

    init();
  }, [selectedJobId, loaded, fetchQRCodeGenJobs, fetchQRCodeTemplateSelection])

  useEffect(() => {
    if (selectedJobId <= 0 || !hasActiveQRCodeRequest) {
      return;
    }

    const interval = setInterval(fetchQRCodeGenJobs, 5000);
    return () => clearInterval(interval);
  }, [selectedJobId, hasActiveQRCodeRequest, fetchQRCodeGenJobs])

  const handleUpload = () => {
    if (!templateFile.type) {
      setMessage('Please upload a template file');
      return;
    }

    if (templateName === '') {
      setMessage('Please enter a template name');
      return;
    }

    if (templateName.includes('/')) {
      setMessage('Template name cannot include "/"');
      return;
    }

    const data = {
      job_id: selectedJobId,
      template: templateName,
      content_type: templateFile.type
    }

    uploadQRCodeTemplate(selectedJobId, data, templateFile).then((result) => {
      setMessage('QR Code template uploaded successfully');
      fetchQRCodeTemplateSelection();
    });
  }

  const handleRemove = () => {
    if (templateRemove.length == 0) {
      setRemoveMessage('Please select template to remove.');
      return;
    }

    const params = templateRemove.map(item => item.template);
    removeQRCodeTemplates(selectedJobId, params).then(() => {
      setRemoveMessage('Templates removed successfully.');
      fetchQRCodeTemplateSelection();
    }).catch(err => {
      console.error(err);
      setRemoveMessage('Templates removal failed.');
    });
  }

  const handleQRJobChange = (field, value) => {
    const obj = Object.assign({}, qrCodeGenJob);
    obj[field] = value;
    setQrCodeGenJob(obj);
  }

  const handleGenerate = async () => {
    if (!qrCodeGenJob.total) {
      setMessage('Please enter a number of QR codes to generate');
      return;
    }

    if (!qrCodeGenJob.email) {
      setMessage('Please enter an email address');
      return;
    }

    if (!isCSVOnly && !selectedTemplate) {
      setMessage('Please select a card template');
      return;
    }

    if (selectedJobId > 0) {
      const downloadType = Number(qrCodeGenJob.download_type || 0);
      const params = {
        ...qrCodeGenJob,
        download_type: downloadType,
        job_id: selectedJobId,
        template: downloadType === CSV_DOWNLOAD_TYPE ? '' : selectedTemplate.name,
        skip_generate_pdf: downloadType === CSV_DOWNLOAD_TYPE ? false : skipGeneratePDF
      };

      generateQRCodeCards(selectedJobId, params).then(() => {
        setShowGenerate(false);
        setLoaded(false);
      })
    }
  }

  const handleTemplateFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (file) {
        var fr = new FileReader();
        fr.onload = function (e) {
          setTemplateFileSrc(fr.result);
        }
        fr.readAsDataURL(file);
        setTemplateFile(file);
      }
    }
  }

  const handleJobChange = (event) => {
    setSelectedJobId(event.target.value);
  }

  const handleTemplateChange = (event) => {
    let template_id = event.target.value;
    handleQRJobChange('template', template_id);

    let templateItem = templateSelection.find((template) => template.value === template_id);
    setSelectedTemplate(templateItem.template);
  }

  const handleDownloadTypeChange = (event) => {
    handleQRJobChange('download_type', Number(event.target.value));
  }

  const handleCloseQRUploadModal = () => {
    setShowTemplate(false);
  }

  const handleCloseQRRemoveModal = () => {
    setShowRemove(false);
  }

  const handleCloseQRGenerateModal = () => {
    setShowGenerate(false);
  }

  const handleRemoveAll = () => {
    setTemplateDisplay([]);
    setTemplateRemove(templateSelection);
  }

  const handleDisplayAll = () => {
    setTemplateDisplay(templateSelection);
    setTemplateRemove([]);
  }


  const handleTemplateRemove = (template) => {
    const templateList = templateDisplay.filter(item => item.template.id != template.template.id)
    setTemplateDisplay(templateList);

    let templateRemoveList = [...templateRemove, template];
    templateRemoveList.sort((a, b) => a.id - b.id);
    setTemplateRemove(templateRemoveList);
  }

  const handleTemplateDisplay = (template) => {
    const templateList = templateRemove.filter(item => item.template.id != template.template.id)
    setTemplateRemove(templateList);

    let templateDisplayList = [...templateDisplay, template];
    templateDisplayList.sort((a, b) => a.id - b.id);
    setTemplateDisplay(templateDisplayList);
  }

  const downloadTypeBodyTemplate = (option) => {
    return (
      <div>
        <Typography>
          {DOWNLOAD_TYPES[option.download_type] || 'Unknown'}
        </Typography>
      </div>
    )
  }

  const dateBodyTemplate = option => {
    if (!option.created_at) {
      return null;
    }

    return (
      <label>{getLocalizedDate(user.locale, option.created_at)}</label>
    )
  }

  const downloadTemplateBodyTemplate = (option) => {
    if (Number(option.download_type) === CSV_DOWNLOAD_TYPE) {
      return <Typography>Not applicable</Typography>;
    }

    if (!option.download_link) {
      const unavailable = option.status === 0 || option.status === 3;
      return <Typography>{unavailable ? 'Unavailable' : 'Pending'}</Typography>;
    }

    return (
      <a href={option.download_link} target='_blank' rel='noopener noreferrer'>Download ZIP</a>
    )
  }

  const csvDownloadTemplateBodyTemplate = (option) => {
    if (!option.csv_download_link) {
      const unavailable = option.status === 0 || option.status === 3;
      return <Typography>{unavailable ? 'Unavailable' : 'Pending'}</Typography>;
    }

    return (
      <a href={option.csv_download_link} target='_blank' rel='noopener noreferrer'>Download CSV</a>
    )
  }

  const cardTemplateBodyTemplate = (option) => {
    return Number(option.download_type) === CSV_DOWNLOAD_TYPE ? 'Not applicable' : option.template;
  }

  const statusBodyTemplate = (option) => {
    return (
      <label>{STATUS[option.status]}</label>
    )
  }

  const skipPDFBodyTemplate = (option) => {
    if (Number(option.download_type) === CSV_DOWNLOAD_TYPE) {
      return <label>Not applicable</label>;
    }

    return (
      <label>{option.skip_generate_pdf ? 'Yes' : 'No'}</label>
    )
  }

  const progressBodyTemplate = option => {
    return (
      <QRCodeGeneratorProgressBar
        qrCodeRequest={option}
        onClick={() => setSelectedQRCodeRequest(option)}
      />
    )
  }

  return (
    <div style={{ padding: '0 2rem' }}>
      <Typography variant="h1">QRCode Generator</Typography>
      <Divider />
      <ButtonGroup>
        <Button variant="contained" color="secondary" onClick={() => setShowTemplate(true)} sx={{ marginRight: '0.5rem' }}>Upload Template</Button>
        <Button variant="contained" color="secondary" onClick={() => setShowRemove(true)} sx={{ marginRight: '0.5rem' }}>Remove Templates</Button>
        <Button variant="contained" color="primary" onClick={() => setShowGenerate(true)}>Generate QR Codes</Button>
      </ButtonGroup>
      <Divider />
      <SelectElement onChange={handleJobChange} options={jobOptions} value={selectedJobId} />
      <Divider />
      <DataTable value={qrCodeGenJobs} dataKey="id" scrollable scrollHeight="400px">
        <Column field="created_at" header="Date" body={dateBodyTemplate} />
        <Column field="total" header="Total" />
        <Column field="download_type" header="Download Type" body={downloadTypeBodyTemplate} />
        <Column field="template" header="Template" body={cardTemplateBodyTemplate} />
        <Column field="email" header="Email" />
        <Column field="progress" header="Progress" body={progressBodyTemplate} />
        <Column field='status' header="Status" body={statusBodyTemplate} />
        <Column field="download_link" header="ZIP Download" body={downloadTemplateBodyTemplate} />
        <Column field="csv_download_link" header="CSV Download" body={csvDownloadTemplateBodyTemplate} />
        <Column field="skip_generate_pdf" header="Generate JPEG" body={skipPDFBodyTemplate} />
      </DataTable>
      {
        selectedQRCodeRequest &&
        <QRCodeGeneratorModal
          qrCodeRequest={selectedQRCodeRequest}
          onClose={() => setSelectedQRCodeRequest(null)}
        />
      }
      {
        showTemplate &&
        <Modal open={true}
          onClose={handleCloseQRUploadModal}
          className={classes.modal}>
          <Box className={templateClass} style={boxStyle}>
            <CloseIcon className={classes.closeIcon} onClick={handleCloseQRUploadModal} />
            <Typography variant={headerVariant}>Template File Details</Typography>
            <Divider />
            <TextField sx={textInput} fullWidth id="outlined-basic" label="Template Name" variant="outlined" value={templateName} onChange={(e) => setTemplateName(e.target.value)} />
            <SelectElement label="Job" onChange={handleJobChange} options={jobOptions} value={selectedJobId} />
            <Divider />
            <label>Template Image</label>
            <Divider />
            <div style={{ marginBottom: "1rem" }}>
              <img src={templateFileSrc || "/img/qr_code_template.jpg"} height="120" style={{ position: "absolute", zIndex: 1 }} />
              <img src="/img/qr_code_overlay.png" height="120" style={{ position: "relative", zIndex: 2 }} />
            </div>
            <UploadFile onFileChange={(e) => { handleTemplateFileChange(e) }} />
            <label>Note: Image must be 2100x1200 @ 300dpi (jpeg).</label>
            <br />
            <a href={templatePsd} target="_blank" >Click here to download PSD template file.</a>
            <Divider />
            <div>
              <Button className={classes.button} variant="contained" color="secondary" onClick={handleCloseQRUploadModal}>Cancel</Button>
              <Button className={classes.button} variant="contained" color="primary" onClick={() => { handleUpload(); }}>Save</Button>
            </div>
          </Box>
        </Modal>
      }
      {
        showRemove &&
        <Modal open={true}
          onClose={handleCloseQRRemoveModal}
          className={classes.modal}>
          <Box className={templateClass} style={boxStyle}>
            <CloseIcon className={classes.closeIcon} onClick={handleCloseQRRemoveModal} />
            <Typography variant={headerVariant}>Remove Template Files</Typography>
            <Divider />
            <SelectElement label="Job" onChange={handleJobChange} options={jobOptions} value={selectedJobId} />
            <Divider />
            <label style={{ marginRight: '10px' }}>Template List</label>
            <Button variant="contained" onClick={handleRemoveAll}>Select All</Button>
            <Divider />
            {
              templateDisplay.map((item) => (
                <img style={{ margin: '2px' }} key={item.value} src={item.template.link} alt={item.template.link} onClick={() => handleTemplateRemove(item)} height="120" />
              ))
            }
            <Divider />
            <label style={{ marginRight: '10px' }}>Template Remove</label>
            <Button variant="contained" onClick={handleDisplayAll}>Select All</Button>
            <Divider />
            {
              templateRemove.map((item) => (
                <img style={{ margin: '2px' }} key={item.value} src={item.template.link} alt={item.template.link} onClick={() => handleTemplateDisplay(item)} height="120" />
              ))
            }
            <Divider />
            <div>
              <Button className={classes.button} variant="contained" color="secondary" onClick={handleCloseQRRemoveModal}>Cancel</Button>
              <Button className={classes.button} variant="contained" color="primary" onClick={() => { handleRemove(); }}>Save</Button>
            </div>
          </Box>
        </Modal>
      }
      {
        showGenerate &&
        <Modal open={true}
          onClose={handleCloseQRGenerateModal}
          className={classes.modal}>
          <Box className={templateClass} style={boxStyle}>
            <CloseIcon className={classes.closeIcon} onClick={handleCloseQRGenerateModal} />
            <Typography variant={headerVariant}>QR Code Generator Details</Typography>
            <Divider />
            <TextField sx={textInput} fullWidth id="outlined-basic" label={isCSVOnly ? 'Number of Codes' : 'Number of Cards'} type='number' variant="outlined" value={qrCodeGenJob.total} onChange={(e) => {
              handleQRJobChange('total', parseInt(e.target.value));
            }} />
            <TextField sx={textInput} fullWidth id="outlined-basic" label="Email Address" variant="outlined" value={qrCodeGenJob.email} onChange={(e) => {
              handleQRJobChange('email', e.target.value);
            }} />
            <SelectElement label="Download Type" onChange={handleDownloadTypeChange} options={downloadTypeOptions} value={qrCodeGenJob.download_type} />
            {!isCSVOnly && (
              <>
                <SelectElement label="Card Template" onChange={handleTemplateChange} options={templateSelection} value={qrCodeGenJob.template} />
                <Divider />
                {selectedTemplate && <img src={selectedTemplate.link} height="120" />}
                <Divider />
                <Checkbox toggle label='QR Codes to JPEG' checked={skipGeneratePDF} onChange={(e, data) => setSkipGeneratePDF(data.checked)}></Checkbox>
                <Divider />
              </>
            )}
            <div>
              <Button className={classes.button} variant="contained" color="secondary" onClick={handleCloseQRGenerateModal}>Cancel</Button>
              <Button className={classes.button} variant="contained" color="primary" onClick={() => handleGenerate()}>Generate</Button>
            </div>
          </Box>
        </Modal>
      }
      <MessageModal
        isOpen={message !== null}
        onClose={() => setMessage(null)}
        title='QR Code Generator'
        message={message}
      />
      <MessageModal
        isOpen={removeMessage !== null}
        onClose={() => setRemoveMessage(null)}
        title='QR Template Removal'
        message={removeMessage}
      />
    </div >
  )
}

const mapStateToProps = state => ({
  user: state.userReducer.user
});

export default connect(mapStateToProps, { uploadQRCodeTemplate, getQRCodeGenTemplates, getQRCodeGenJobs, generateQRCodeCards, removeQRCodeTemplates })(EmployeeQRGenerator);
